import { uniqid } from '../utils/utils';

declare global {
    interface DocumentEventMap {
        'add_flash': CustomEvent<{ type: 'success'|'warning'|'danger', message: string }>;
    }
}

interface HTMLFlashElement extends HTMLElement {
    name: string,
}

type FlashMessagesType = {
    $flashesContainer: HTMLElement|null,
    $flashMessages: HTMLFlashElement[],
    autocloseTimeout: number,
    timeouts: Record<string, ReturnType<typeof setTimeout>>,
    init(): void,
    add(type: 'success'|'warning'|'danger', message: string): void,
    show($flashMessage: HTMLFlashElement): void,
    remove($flashMessage: HTMLFlashElement) : void,
    setTheTimeout($flashMessage: HTMLFlashElement) : ReturnType<typeof setTimeout>,
};

const FlashMessages: FlashMessagesType = {
    $flashesContainer: null,
    $flashMessages: [],
    autocloseTimeout: 5000, // in ms
    timeouts: {},

    init() : void {
        this.$flashesContainer = document.querySelector<HTMLElement>('#js-flashes-container');
        if (!this.$flashesContainer) {
            return;
        }

        this.$flashMessages = Array.from(this.$flashesContainer.querySelectorAll<HTMLFlashElement>('.js-flash'));
        this.$flashMessages.forEach($flashMessage => {
            this.show($flashMessage);
        });

        document.addEventListener('add_flash', evt => {
            this.add(evt.detail.type, evt.detail.message);
        });
    },

    /**
     * Add a flash message
     * 
     * @param {'success'|'warning'|'danger'} type
     * @param {string} message
     * 
     * @return {void}
     */
    add(type: 'success'|'warning'|'danger', message: string) : void {
        const $template = document.querySelector<HTMLTemplateElement>('#js-flash-message-template');

        if (!$template) {
            return;
        }

        const tmpDiv = document.createElement('div');
        tmpDiv.innerHTML = $template.innerHTML
            .replace('%type%', type)
            .replace('%message%', message);

        if ('success' !== type) {
            tmpDiv.querySelector<HTMLElement>('.js-icon-success')?.remove();
        }

        const $flashMessage = tmpDiv.firstElementChild as HTMLFlashElement;
        this.$flashesContainer?.append($flashMessage);

        this.show($flashMessage);
    },

    /**
     * Show the flash message
     * 
     * @param {HTMLElement} $flashMessage
     * 
     * @return {void}
     */
    show($flashMessage: HTMLFlashElement) : void {
        $flashMessage.name = uniqid();

        this.timeouts[$flashMessage.name] = this.setTheTimeout($flashMessage);

        const $closeButton = $flashMessage.querySelector<HTMLButtonElement>('.js-close-button');

        // Stop the timeout when mouseenter
        $flashMessage.addEventListener('mouseenter', () => {
            if (typeof this.timeouts[$flashMessage.name] !== 'undefined') {
                clearTimeout(this.timeouts[$flashMessage.name]);
            }
        });

        // Restart the timeout when mouseleave
        $flashMessage.addEventListener('mouseleave', () => {
            this.timeouts[$flashMessage.name] = this.setTheTimeout($flashMessage);
        });

        $closeButton?.addEventListener('click', () => {
            this.remove($flashMessage);
        });
    },

    /**
     * Remove the flash message from the DOM
     * 
     * @param {HTMLElement} $flashMessage 
     * 
     * @return {void}
     */
    remove($flashMessage: HTMLFlashElement) : void {
        if (typeof this.timeouts[$flashMessage.name] !== 'undefined') {
            clearTimeout(this.timeouts[$flashMessage.name]);
        }

        $flashMessage.classList.add('transition-out');
        $flashMessage.addEventListener('transitionend', () => {
            $flashMessage.remove();
        });
    },

    /**
     * Set the timeout before removing the flash message from the DOM
     * 
     * @param {HTMLElement} $flashMessage 
     * 
     * @return {ReturnType<typeof setTimeout>}
     */
    setTheTimeout($flashMessage: HTMLFlashElement) : ReturnType<typeof setTimeout> {
        return setTimeout(() => {
            this.remove($flashMessage);
        }, this.autocloseTimeout);
    },
};

export default FlashMessages;
